import { useState, useCallback } from "react";
import { extractErrorMessage } from "@/api/axios";

interface UseApiCallOptions {
  onSuccess?: (message?: string) => void;
  onError?: (message: string) => void;
}

interface UseApiCallReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  execute: (...args: any[]) => Promise<T | undefined>;
  reset: () => void;
}

export function useApiCall<T>(
  apiFn: (...args: any[]) => Promise<T>,
  options?: UseApiCallOptions,
): UseApiCallReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (...args: any[]): Promise<T | undefined> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiFn(...args);
        setData(result);
        options?.onSuccess?.();
        return result;
      } catch (err) {
        const msg = extractErrorMessage(err);
        setError(msg);
        options?.onError?.(msg);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [apiFn, options?.onSuccess, options?.onError],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, execute, reset };
}
